import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import ChatBot from './ChatBot';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';

// Sophisticated chat widget with premium styling (micro-interactions removed for stability)
const ChatWidget: React.FC = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const toggleChat = () => {
    console.log('Chat button clicked, current state:', isChatOpen);
    // setIsChatOpen(!isChatOpen); // DISABLED_FOR_PRODUCTION
    console.log('Chat state should be:', !isChatOpen);
  };

  return (
    <>
      <ChatButton 
        onClick={toggleChat} 
        isOpen={isChatOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <ButtonContent isOpen={isChatOpen}>
          {isChatOpen ? (
            <CloseIcon>✕</CloseIcon>
          ) : (
            <>
              <ChatIcon>💬</ChatIcon>
              <PulseRing isHovered={isHovered} />
              {!isHovered && (
              <NotificationDot />
              )}
            </>
          )}
        </ButtonContent>
        
        {!isChatOpen && (
          <Tooltip isVisible={isHovered}>
            Discover your path with Rovers Guide
          </Tooltip>
        )}
      </ChatButton>
      
      <ChatBot isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </>
  );
};

// Elegant pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Premium chat button with sophisticated hover effects
const ChatButton = styled.button<{ isOpen: boolean }>`
  position: fixed;
  bottom: ${getSpacing('xl')};
  right: ${getSpacing('xl')};
  width: 70px;
  height: 70px;
  border-radius: ${DesignSystem.borderRadius.full};
  background: ${props => props.isOpen 
    ? 'rgba(255, 255, 255, 0.95)' 
    : 'rgba(255, 255, 255, 0.9)'
  };
  backdrop-filter: blur(20px);
  color: ${props => props.isOpen ? getColor('primary.700') : getColor('primary.600')};
  border: 2px solid ${getColor('primary.200')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.bounce};
  z-index: 9999;
  overflow: visible;
  
  &:hover {
    transform: scale(1.1) translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    background: rgba(255, 255, 255, 0.98);
    border-color: ${getColor('primary.300')};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus {
    outline: none;
    box-shadow: ${getShadow('large')}, 0 0 0 3px ${getColor('accent.200')};
  }
  
  @media (max-width: 768px) {
    bottom: ${getSpacing('lg')};
    right: ${getSpacing('lg')};
    width: 60px;
    height: 60px;
  }
`;

const ButtonContent = styled.div<{ isOpen: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  
  ${props => !props.isOpen && css`
    animation: ${float} 3s ease-in-out infinite;
  `}
`;

const ChatIcon = styled.span`
  font-size: 1.5rem;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
`;

const CloseIcon = styled.span`
  font-size: 1.25rem;
  font-weight: 300;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
`;

// Sophisticated pulse ring effect
const PulseRing = styled.div<{ isHovered: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border: 2px solid ${getColor('accent.400')};
  border-radius: ${DesignSystem.borderRadius.full};
  ${css`
    animation: ${pulse} 2s ease-out infinite;
  `}
  opacity: ${props => props.isHovered ? 0.8 : 0.4};
  transition: opacity ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
`;

// Elegant tooltip with premium styling
const Tooltip = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 100%;
  right: 0;
  background: ${getColor('primary.900')};
  color: white;
  padding: ${getSpacing('sm')} ${getSpacing('md')};
  border-radius: ${DesignSystem.borderRadius.lg};
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: ${getShadow('medium')};
  margin-bottom: ${getSpacing('sm')};
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transform: translateX(${props => props.isVisible ? '0' : '10px'});
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  z-index: ${DesignSystem.zIndex.tooltip};
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: ${getSpacing('lg')};
    border: 6px solid transparent;
    border-top-color: ${getColor('primary.900')};
  }
  
  ${props => css`
    animation: ${props.isVisible ? fadeIn : 'none'} ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  `}
  
  @media (max-width: 768px) {
    display: none; // Hide on mobile for cleaner experience
  }
`;

// Simple notification dot
const NotificationDot = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 6px;
  height: 6px;
  background: ${getColor('accent.500')};
  border-radius: 50%;
  animation: ${pulse} 2s infinite;
`;

export default ChatWidget;